import { auth, db } from "../config/firebase";
import { Auth } from "firebase/auth";
import {
  Empty,
  Operator,
  Model,
  Resource,
} from "@beyondrealityapp/core/shared/constants";
import { Filter } from "../utils/filter/classes";
import {
  and,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  Firestore,
  runTransaction,
  query,
  where,
} from "firebase/firestore";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { GoalClass } from "@beyondrealityapp/core/goal/classes";
import {
  CheckinProgress,
  GoalField,
  GoalHistorySource,
  GoalStatus,
} from "@beyondrealityapp/core/goal/constants";
import {
  GoalFormType,
  GoalFirestoreType,
  GoalHistoryType,
} from "@beyondrealityapp/core/goal/types";
import { functions } from "../config/firebase";
import { httpsCallable } from "firebase/functions";

// What to do with the sub-goals of a deleted goal: "keep" makes them
// top-level goals, "delete" deletes them and their own sub-goals.
export type SubGoalsOnDelete = "keep" | "delete";

interface deleteGoalArgs {
  goalId: string;
  parentGoalId: string;
  subGoals?: SubGoalsOnDelete;
}

interface generateSubgoalsArgs {
  parentGoal: Partial<GoalFormType>;
  existingSubgoals?: Array<{
    content: string;
    status: string;
    unit: string;
    target: string;
    current: string;
  }>;
}

/** One goal's answer in a weekly check-in. */
export type CheckinAnswer = {
  goalId: string;
  /** The new current value; left out for goals without a unit or target. */
  current?: string;
  /** Optional for a goal marked completed; required otherwise. */
  progress?: CheckinProgress;
  /** Marks the goal completed. */
  completed?: boolean;
};

interface GenerateSubgoalsResponse {
  data?: {
    goalIds: string[];
    message: string;
  };
  fault?: {
    code: string;
    message: string;
  };
}


export const goalsApi = createApi({
  reducerPath: "goalsApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Goal"],
  endpoints: (builder) => ({
    fetchGoals: builder.query({
      queryFn: async (filter: Filter) => {
        try {
          const goals = await _retrieveGoals(filter);
          return { data: goals };
        } catch (error) {
          return { error: error as Error };
        }
      },
      providesTags: (result, error, arg) =>
        result
          ? [...result.map(({ id }) => ({ type: "Goal" as const, id })), "Goal"]
          : ["Goal"],
    }),
    fetchGoal: builder.query({
      queryFn: async (id?: string) => {
        if (!id) {
          return { error: new Error("No goal id provided") };
        }
        try {
          const goal = await _fetchGoal(id);
          return { data: goal };
        } catch (error) {
          return { error: error as Error };
        }
      },
      providesTags: (result, error, arg) => [{ type: "Goal", id: result?.id }],
    }),
    createUpdateGoal: builder.mutation({
      queryFn: async (goalForm: Partial<GoalFormType>) => {
        try {
          const goal = await _persistGoal(goalForm);
          return { data: goal };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: (result, error, arg) => [{ type: "Goal", id: arg.id }],
    }),
    saveCheckin: builder.mutation({
      queryFn: async (answers: CheckinAnswer[]) => {
        try {
          await _saveCheckin(answers);
          return { data: answers.length };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["Goal"],
    }),
    deleteGoal: builder.mutation({
      queryFn: async ({ goalId, parentGoalId, subGoals }: deleteGoalArgs) => {
        try {
          await _removeGoal(goalId, parentGoalId, subGoals);
          return { data: goalId };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: (result, error, arg) => [
        arg.parentGoalId || arg.subGoals
          ? "Goal"
          : { type: "Goal", id: arg?.goalId },
      ],
    }),
    generateSubgoals: builder.mutation<
      GenerateSubgoalsResponse,
      generateSubgoalsArgs
    >({
      queryFn: async ({ parentGoal, existingSubgoals }: generateSubgoalsArgs) => {
        try {
          // Convert Dayjs dates to ISO strings for serialization
          const serializedParentGoal = {
            ...parentGoal,
            startDate: parentGoal.startDate?.toISOString() || null,
            endDate: parentGoal.endDate?.toISOString() || null,
          };
          
          const generateSubgoalsFn = httpsCallable<
            { parentGoal: any; existingSubgoals?: any[] },
            GenerateSubgoalsResponse
          >(functions, "generateSubgoals");
          const result = await generateSubgoalsFn({ 
            parentGoal: serializedParentGoal,
            existingSubgoals
          });
          return { data: result.data };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["Goal"],
    }),
  }),
});

export const {
  useFetchGoalsQuery,
  useFetchGoalQuery,
  useCreateUpdateGoalMutation,
  useDeleteGoalMutation,
  useGenerateSubgoalsMutation,
  useSaveCheckinMutation,
} = goalsApi;

/**
 * Saves a weekly check-in: each answered goal gets its new current value
 * (and status Completed when marked so) and a check-in entry in its
 * history, in one transaction. There is no
 * separate check-in document; the history entries are the check-in.
 */
export const _saveCheckin = async (answers: CheckinAnswer[]) => {
  const date = new Date();
  await runTransaction(db, async (transaction) => {
    // Firestore transactions need every read before the first write
    const refs = answers.map((answer) => doc(db, Model.GOALS, answer.goalId));
    const snapshots = await Promise.all(refs.map((ref) => transaction.get(ref)));

    answers.forEach((answer, index) => {
      const snapshot = snapshots[index];
      // A goal deleted since the check-in opened has nothing to update
      if (!snapshot.exists()) return;

      const goal = snapshot.data() as Partial<GoalFirestoreType>;
      const current = answer.current ?? goal.current ?? "";
      const completes = Boolean(answer.completed) && goal.status !== GoalStatus.COMPLETED;
      const status = completes ? GoalStatus.COMPLETED : goal.status ?? "";
      const entry: GoalHistoryType = GoalClass.historyEntry({
        date,
        current,
        status,
        source: GoalHistorySource.CHECKIN,
        progress: answer.progress,
      });
      const update: { [field: string]: string | Date | unknown[] } = {
        history: [...(goal.history ?? []), entry],
        "metaData.updatedAt": date,
      };
      const childUpdate: { [field: string]: string } = {};
      if (current !== goal.current) {
        update.current = current;
        childUpdate.current = current;
      }
      if (completes) {
        update.status = status;
        update.completedAt = date;
        childUpdate.status = status;
      }
      // Parents keep a summary of each child goal
      if (goal.parent && Object.keys(childUpdate).length > 0) {
        transaction.set(
          doc(db, Model.GOALS, goal.parent),
          { children: { [answer.goalId]: childUpdate } },
          { merge: true }
        );
      }
      transaction.update(refs[index], update);
    });
  });
};

// TODO - Remove parentGoalId from the deleteGoalArgs
const _removeGoal = async (
  goalId: string,
  parentGoalId: string,
  subGoals?: SubGoalsOnDelete
) => {
  const parentQuerySnapshot = await _queryParentGoal(db, auth, parentGoalId);
  const directSubGoalIds = subGoals ? await _querySubGoalIds(goalId) : [];
  const descendantIds =
    subGoals === "delete"
      ? await _collectDescendantIds(goalId, directSubGoalIds)
      : directSubGoalIds;
  return new Promise<void>((resolve, reject) => {
    runTransaction(db, async (transaction) => {
      if (parentQuerySnapshot && !parentQuerySnapshot.empty) {
        parentQuerySnapshot.forEach((parentDoc) => {
          transaction.update(parentDoc.ref, {
            [`children.${goalId}`]: deleteField(),
          });
        });
      }
      descendantIds.forEach((id) => {
        const ref = doc(db, Model.GOALS, id);
        if (subGoals === "delete") {
          transaction.delete(ref);
        } else {
          transaction.update(ref, {
            [GoalField.PARENT]: Empty.STRING,
            [GoalField.PARENT_NAME]: Empty.STRING,
          });
        }
      });
      transaction.delete(doc(db, Model.GOALS, goalId));
    })
      .then(() => {
        resolve();
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
};

const _persistGoal = async (goalForm: Partial<GoalFormType>) => {
  const goal = GoalClass.fromForm(goalForm);
  // A new goal gets its id from init(); only an existing one can be read back
  const isExisting = Boolean(goal.id);

  goal.init(auth);

  const parentQuerySnapshot = await _queryParentGoal(db, auth, goal.parent);
  const goalRef = doc(db, Model.GOALS, goal.id);
  parentQuerySnapshot?.forEach((parentDoc) => {
    goal.parentName = parentDoc.data().content ?? Empty.STRING;
  });

  return new Promise<GoalClass>((resolve, reject) => {
    runTransaction(db, async (transaction) => {
      // History is kept from the stored goal, so a form opened before a
      // check-in can't drop it; a changed current or status adds an entry
      const stored = isExisting ? await transaction.get(goalRef) : undefined;
      if (stored?.exists()) {
        const before = stored.data() as Partial<GoalFirestoreType>;
        goal.history = GoalClass.historyFromFirestore(before.history);
        if ((before.current ?? "") !== goal.current || before.status !== goal.status) {
          goal.history.push(
            GoalClass.historyEntry({
              current: goal.current,
              status: goal.status,
              source: GoalHistorySource.EDIT,
            })
          );
        }
      }
      // Keep the sub-goals' copy of this goal's name in sync. Reads come
      // before writes in a transaction; missing sub-goals are skipped.
      const subGoalRefs = Object.keys(goal.children).map((id) =>
        doc(db, Model.GOALS, id)
      );
      const subGoalDocs = await Promise.all(
        subGoalRefs.map((ref) => transaction.get(ref))
      );
      subGoalDocs.forEach((subGoalDoc, index) => {
        if (
          subGoalDoc.exists() &&
          subGoalDoc.data().parentName !== goal.content
        ) {
          transaction.update(subGoalRefs[index], {
            [GoalField.PARENT_NAME]: goal.content,
          });
        }
      });
      if (parentQuerySnapshot && !parentQuerySnapshot.empty) {
        parentQuerySnapshot.forEach((parentDoc) => {
          transaction.set(
            parentDoc.ref,
            {
              children: {
                [goal.id]: {
                  name: goal.content,
                  status: goal.status,
                  unit: goal.kpi,
                  target: goal.target,
                  current: goal.current,
                },
              },
            },
            { merge: true }
          );
        });
      }
      transaction.set(goalRef, goal.toFirestore());
    })
      .then(() => {
        resolve(goal);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const _queryParentGoal = async (
  db: Firestore,
  auth: Auth,
  parentGoalId: string
) => {
  if (parentGoalId === "") {
    return undefined;
  }
  const goalsRef = collection(db, Model.GOALS);
  const q = query(
    goalsRef,
    and(
      where(GoalField.UID, Operator.EQUAL_TO, auth.currentUser?.uid),
      where(Resource.ID, Operator.EQUAL_TO, parentGoalId)
    )
  );
  return await getDocs(q);
};

const _querySubGoalIds = async (goalId: string) => {
  const q = query(
    collection(db, Model.GOALS),
    and(
      where(GoalField.UID, Operator.EQUAL_TO, auth.currentUser?.uid),
      where(GoalField.PARENT, Operator.EQUAL_TO, goalId)
    )
  );
  const snapshot = await getDocs(q);
  const ids: string[] = [];
  snapshot.forEach((subGoalDoc) => {
    ids.push(subGoalDoc.id);
  });
  return ids;
};

// Breadth-first walk down the sub-goal tree. The visited set guards against
// cycles in malformed data.
const _collectDescendantIds = async (goalId: string, subGoalIds: string[]) => {
  const visited = new Set<string>([goalId]);
  let level = subGoalIds;
  while (level.length > 0) {
    level = level.filter((id) => !visited.has(id));
    level.forEach((id) => visited.add(id));
    const nextLevels = await Promise.all(level.map(_querySubGoalIds));
    level = nextLevels.flat();
  }
  visited.delete(goalId);
  return Array.from(visited);
};

const _retrieveGoals = async (filter: Filter) => {
  const q = filter.createQuery(db);
  const goalDocs = await getDocs(q);
  const tempGoals: GoalClass[] = [];
  goalDocs.forEach((doc) => {
    tempGoals.push(
      GoalClass.fromFirestore({
        ...doc.data(),
        id: doc.id,
      } as GoalFirestoreType)
    );
  });
  return tempGoals;
};

const _fetchGoal = async (id: string) => {
  return new Promise<GoalClass>((resolve, reject) => {
    let goal = {} as GoalClass;
    const goalRef = doc(db, Model.GOALS, id);
    getDoc(goalRef)
      .then((doc) => {
        if (doc.exists()) {
          goal = GoalClass.fromFirestore({
            ...doc.data(),
            id: doc.id,
          } as GoalFirestoreType);
        }
        resolve(goal);
      })
      .catch((error) => {
        reject(error);
      });
  });
};
